// Local ports of speed_member/road.py and divinity.py. Decisions require fresh game samples.
export const gameplayTasks = [
  { id: 'divinity-monsters', name: '中枢怪物挑战', description: '遍历可挑战怪物，确认战斗并按领取设置收取奖励。' },
  { id: 'road-normal', name: '斗罗之路 · 普通', description: '选择最高可挑战关卡，处理剧情、挑战及结算，按实际通关计数。' },
  { id: 'road-hard', name: '斗罗之路 · 困难', description: '使用困难难度的进度与按钮状态，按实际通关计数。' },
  { id: 'hunt-tower', name: '猎魂塔', description: '自动挑战、处理结算，入口未开放时等待。' },
  { id: 'hell-road', name: '地狱之路', description: '根据实际层数挑战，战败后等待再试。' },
  { id: 'soul-tower', name: '魂师之塔', description: '自动挑战及处理本次战报，按确认通关次数计数。' },
  { id: 'dragon-relic', name: '龙神遗迹', description: '先在游戏中选择遗迹类型；挑战失败时暂停。' },
]
const wait = message => ({ state: 'running', message })
const done = message => ({ state: 'completed', message })
const pause = message => ({ state: 'paused', message })
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b)

class Identity {
  check(s) {
    if (!s || !s.document || !s.identity || !Number.isSafeInteger(s.sequence)) throw new Error('未读到完整角色状态')
    if (this.owner && (this.owner !== s.identity || this.doc !== s.document)) throw new Error('角色或游戏页面已变化，请重新开始')
    if (this.sequence && this.sequence >= s.sequence) throw new Error('游戏状态尚未更新')
    this.owner = s.identity; this.doc = s.document; this.sequence = s.sequence
  }
}

export class TowerPolicy extends Identity {
  constructor(task, loops, now) { super(); this.task = task; this.loops = loops; this.now = now; this.wins = 0; this.scope = null; this.pending = null; this.inFlight = false; this.reportPending = false; this.seen = new Set(); this.ready = 0; this.infoRequests = 0; this.nextInfo = 0 }
  action(action, s) {
    if (!s.actions.includes(action) || ['challenge','next'].includes(action) && !s.managed) return wait('等待玩法按钮就绪')
    return { state: 'running', action, proof: s, message: { open: '打开玩法入口', challenge: '挑战当前层', next: '继续下一层', exit: '退出本次结算', report: '查看本次战报', close_report: '关闭本次战报', dismiss_entry: '确认玩法入口', load_info: '同步猎魂塔进度' }[action] }
  }
  acknowledge(c) {
    this.pending = { action: c.action, before: JSON.parse(JSON.stringify(c.proof)), at: this.now() }
    if (['challenge','next'].includes(c.action)) this.inFlight = true
    if (c.action === 'report') this.reportPending = true
    if (c.action === 'close_report') { this.reportPending = false; this.ready = this.now() + 5000 }
    if (c.action === 'exit' && c.proof.variant !== 'infinity') this.ready = this.now() + 5000
  }
  observe(s) {
    this.check(s)
    if (!Array.isArray(s.actions) || !['none','win','lose'].includes(s.result)) throw new Error('当前玩法状态尚不支持')
    if (this.scope !== null && this.scope !== s.scope) throw new Error('遗迹类型已变化，请重新开始')
    const now = this.now()
    if (s.scene && !s.scene.connected) return pause('游戏已断线，重新连接后继续')
    if (this.pending) {
      const { before, at } = this.pending
      const changed = s.busy || ['supported','result_id','level','panel','report','entry_notice'].some(k => !equal(s[k], before[k]))
      if (changed) this.pending = null
      else if (now - at >= 30000) return pause('操作尚未确认，请检查游戏后继续')
      else return wait('等待游戏确认操作')
    }
    if (this.task === 'hunt-tower' && s.entry_open === false && s.result === 'none' && !s.report) return wait('猎魂塔尚未开放')
    if (!s.supported) {
      if (this.task !== 'hunt-tower') return wait('请先在游戏中打开对应玩法')
      if (s.busy) return wait('等待当前场景就绪')
      if (s.entry_notice) return this.action('dismiss_entry', s)
      if (s.variant === 'infinity' && s.actions.includes('load_info') && this.infoRequests < 3 && now >= this.nextInfo) { this.infoRequests++; this.nextInfo = now + 5000; return this.action('load_info', s) }
      return !s.panel ? this.action('open', s) : wait('等待猎魂塔资料同步')
    }
    this.infoRequests = 0
    if (this.task === 'dragon-relic' && !s.scope) return wait('请先在游戏中选择遗迹类型')
    this.scope = s.scope
    if (s.busy) return wait('战斗或切图中，等待结算')
    if (s.entry_notice) return this.action('dismiss_entry', s)
    if (s.report) return this.reportPending ? this.action('close_report', s) : pause('请先关闭手动打开的战报')
    if (s.result !== 'none') {
      if (!s.result_id || s.result_level <= 0 || s.result === 'win' && s.level < s.result_level) return wait('等待完整结算与进度同步')
      if (!this.seen.has(s.result_id)) {
        this.seen.add(s.result_id); if (this.seen.size > 256) this.seen = new Set([s.result_id])
        if (s.result === 'win' && this.inFlight) this.wins++
        this.inFlight = false; this.ready = now + (s.result === 'win' ? 1000 : 5000)
      }
      if (this.loops && this.wins >= this.loops || s.maximum && s.result === 'win') return s.variant === 'infinity' ? this.action('exit', s) : done(`已确认通关 ${this.wins} 次`)
      if (s.result === 'lose' && this.task === 'dragon-relic') return pause('本关挑战失败，已暂停')
      if (s.variant === 'infinity' && s.actions.includes('exit') && (s.result === 'lose' || !s.actions.includes('next'))) return this.action('exit', s)
      if (now < this.ready) return wait('等待间隔后继续')
      if (this.task === 'soul-tower' && !(s.result === 'win' && s.actions.includes('next')) && s.actions.includes('report')) return this.action('report', s)
      return this.action(s.result === 'win' ? 'next' : 'exit', s)
    }
    if (this.loops && this.wins >= this.loops || s.maximum) return done(`已确认通关 ${this.wins} 次`)
    if (now < this.ready || !s.active_map) return wait('等待下一次挑战')
    if (this.task !== 'dragon-relic' && !s.panel) return this.action('open', s)
    return this.action('challenge', s)
  }
  resume() { if (this.pending) this.pending.at = this.now() }
}

export class RoadPolicy extends Identity {
  constructor(task, loops, now) { super(); this.d = task === 'road-normal' ? 1 : 2; this.loops = loops; this.now = now; this.pending = null; this.wins = 0; this.seen = new Set(); this.ready = 0; this.mode = false; this.refresh = 0; this.started = null }
  target(s) {
    const node = s.nodes[this.d - 1]
    if (node.maximum) return node
    return [node, ...(node.available || [])].filter(n => n.id > 0 && n.status === 1).sort((a, b) => b.id - a.id)[0] || node
  }
  action(action, s) { return { state: 'running', action, proof: { sample: s, difficulty: this.d, node: this.target(s).id }, message: { open: '打开斗罗之路', mode: '切换难度', detail: '查看最高可挑战关卡', challenge: '发起关卡挑战', exit: '确认结算', advance: '继续当前剧情', refresh: '同步可挑战关卡' }[action] } }
  acknowledge(c) { this.pending = { action: c.action, before: JSON.parse(JSON.stringify(c.proof.sample)), at: this.now() }; if (c.action === 'challenge') this.started = c.proof.node; if (c.action === 'mode') this.mode = true }
  observe(s) {
    this.check(s)
    if (!Array.isArray(s.nodes) || s.nodes.length !== 2 || !s.result) throw new Error('当前游戏版本的斗罗之路尚不支持')
    if (!s.connected) return pause('游戏已断线，重新连接后继续')
    if (s.panel_mode < 0) this.mode = false
    const now = this.now(), r = s.result, flow = s.flow || {}, node = this.target(s)
    if (this.pending) {
      const { action, before, at } = this.pending
      const changed = action === 'exit' ? s.busy || r.id !== before.result.id : ['busy', 'normal', 'panel_mode', 'detail_id', 'result', 'flow'].some(k => !equal(s[k], before[k])) || !equal(s.nodes[this.d - 1], before.nodes[this.d - 1]) || action === 'mode' && s.panel_mode === this.d - 1
      if (changed) this.pending = null
      else if (now - at < 30000) return wait('等待游戏确认操作')
      else return pause('上次操作未确认，请检查游戏后继续')
    }
    if (flow.kind) return flow.difficulty === this.d && flow.ready ? this.action('advance', s) : wait('等待当前剧情结束')
    if (s.busy) return wait('战斗或进图中，等待结算')
    if (r.id) {
      if (r.difficulty !== this.d) return pause('请先处理其他难度的结算')
      if (!this.seen.has(r.id)) {
        this.seen.add(r.id)
        if (r.won && r.node === this.started) this.wins++
        this.started = null; this.ready = now + (r.won ? 1000 : 5000)
      }
      return r.ready && s.managed ? this.action('exit', s) : wait('等待结算按钮')
    }
    if (this.loops && this.wins >= this.loops) return done(`已确认通关 ${this.wins} 次`)
    if (node.maximum) return done('当前难度已达最高关卡')
    if (now < this.ready || !s.normal) return wait('等待进入下一关')
    if (s.panel_mode < 0) return s.open_ready ? this.action('open', s) : wait('等待玩法开放')
    if (!this.mode || s.panel_mode !== this.d - 1) return s.mode_available && s.mode_available[this.d - 1] ? this.action('mode', s) : wait('等待难度开放')
    if (!node.id || node.status !== 1) {
      if (s.refresh_ready && now >= this.refresh) { this.refresh = now + 10000; return this.action('refresh', s) }
      return wait('等待可挑战关卡同步')
    }
    if (s.detail_id !== node.id) return this.action('detail', s)
    return s.detail_ready && s.managed ? this.action('challenge', s) : wait('等待挑战按钮就绪')
  }
  resume() { if (this.pending) this.pending.at = this.now() }
}

export async function divinityGuard(s, target) {
  const keys = ['document','identity','season','supported','normal','connected','changing_map','one_key_busy','detail','challenge','click_ready','entry','guide','reward','failure','capacity','travel']
  const values = keys.map(k => s[k]); values.push(s.targets.find(t => t.id === target) || null)
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(values)))
  return Array.from(new Uint8Array(hash), n => n.toString(16).padStart(2, '0')).join('')
}

export class DivinityPolicy extends Identity {
  constructor(loops, now, options = {}) {
    super(); this.loops = loops; this.now = now; this.pending = null; this.visited = new Set(); this.started = new Set(); this.wins = 0; this.sent = 0; this.ready = 0; this.season = null
    this.doubleReward = options.doubleReward === true
    this.doubleRewardThreshold = options.doubleRewardThreshold == null ? 50 : options.doubleRewardThreshold
    if (!Number.isInteger(this.doubleRewardThreshold) || this.doubleRewardThreshold < 0 || this.doubleRewardThreshold > 100000000) throw new Error('双倍威望门槛需为 0 至 100000000 的整数')
  }
  key(t) { return `${t.id}:${t.refresh}` }
  async action(action, s, t) {
    const target = t ? t.id : (action === 'claim' ? s.reward.target : s.failure.target)
    const tier = action === 'claim' && this.doubleReward && s.reward.value > this.doubleRewardThreshold ? 2 : 1
    return { state: 'running', action, proof: { document: s.document, identity: s.identity, season: s.season, target, refresh: t ? t.refresh : 0, round: t ? t.round : 0, index: action === 'claim' ? s.reward.index : 0, value: action === 'claim' ? s.reward.value : 0, tier, guard: await divinityGuard(s, target) }, message: { close_guide: '收起中枢介绍', open_entry: '打开中枢入口', enter_map: '进入神界中枢', open_target: '查看可挑战怪物', challenge: '挑战当前怪物', open_reward: '查看本次奖励', claim: tier === 2 ? '双倍领取本次奖励' : '单倍领取本次奖励', exit: '退出失败结算' }[action] }
  }
  acknowledge(c) { this.pending = { action: c.action, proof: c.proof, at: this.now() } }
  async observe(s) {
    this.check(s)
    if (s.schema !== 2 || !Array.isArray(s.targets) || !s.entry || !s.capacity || !s.travel) throw new Error('当前游戏版本的中枢尚不支持')
    if (!s.connected) return pause('游戏已断线，重新连接后继续')
    if (this.season !== null && this.season !== s.season) throw new Error('中枢赛季已变化，请重新开始')
    const now = this.now(), entry = s.entry, moving = s.changing_map || s.one_key_busy
    if (this.pending) {
      const { action: a, proof: p, at } = this.pending, t = s.targets.find(t => t.id === p.target)
      let accepted = false
      if (a === 'open_entry') accepted = entry.shown || entry.inside
      if (a === 'enter_map') accepted = entry.inside
      if (a === 'close_guide') accepted = !s.guide.shown
      if (a === 'open_target') accepted = s.detail === p.target
      if (a === 'open_reward') accepted = s.reward.target === p.target
      if (a === 'claim') accepted = s.reward.target !== p.target || s.reward.index !== p.index
      if (a === 'exit') accepted = s.failure.target !== p.target
      if (a === 'challenge') {
        accepted = !!(t && (t.fighting || t.round !== p.round || t.rewards.includes(1)) || s.reward.target === p.target || s.failure.target === p.target)
        if (accepted) { this.visited.add(`${p.target}:${p.refresh}`); this.started.add(p.target); this.sent++ }
        else if (!t || t.refresh !== p.refresh) { this.visited.add(`${p.target}:${p.refresh}`); accepted = true }
      }
      if (accepted) { this.pending = null; if (['challenge','claim','exit'].includes(a)) this.ready = now + (a === 'exit' ? 5000 : 1000) }
      else if (now - at >= 45000) return pause('上次中枢操作尚未确认，请检查游戏后继续')
      else return wait('等待游戏确认中枢操作')
    }
    if (s.guide.shown) return s.guide.closable ? this.action('close_guide', s) : wait('等待玩法介绍就绪')
    if (!entry.inside && !s.reward.target && !s.failure.target) {
      if (moving || !s.normal) return wait('等待当前战斗或切图结束')
      if (!(entry.feature_open && entry.data_ready && entry.open_callable && entry.enter_callable)) return wait('等待中枢开放或加载')
      return entry.shown ? entry.enter_button ? this.action('enter_map', s) : wait('等待进入按钮') : this.action('open_entry', s)
    }
    if (!s.supported || !(s.capacity.limit > 0)) return wait('等待中枢怪物列表')
    this.season = s.season
    if (s.reward.target || s.failure.target) {
      const isReward = !!s.reward.target, outcome = isReward ? s.reward : s.failure
      if (!this.started.has(outcome.target)) return pause('请先手动处理任务开始前的结算')
      const wantsDouble = isReward && this.doubleReward && outcome.value > this.doubleRewardThreshold
      if (wantsDouble && !(outcome.double_button && outcome.can_more && outcome.password_ready)) return pause('双倍资源不足或需要密码确认，请处理后继续')
      if (wantsDouble) return this.action('claim', s)
      return (isReward ? outcome.single_button : outcome.available) ? this.action(isReward ? 'claim' : 'exit', s) : wait('等待结算按钮')
    }
    if (moving || !s.normal) return wait('移动或战斗中')
    const reward = s.targets.find(t => this.started.has(t.id) && t.rewards.includes(1))
    if (reward) return this.action('open_reward', s, reward)
    if (now < this.ready) return wait('等待挑战间隔')
    const travel = s.travel
    const candidate = s.targets.find(t => !this.visited.has(this.key(t)) && !(t.max_round > 0 && t.round >= t.max_round) && !t.rewards.includes(1) && !t.fighting && (travel.new_season || travel.can_move) && (travel.new_season || t.city === travel.city || travel.cities.includes(t.city)))
    if (candidate) {
      if (s.capacity.active >= s.capacity.limit) return wait('挑战位置已满，等待结算')
      if (s.detail !== candidate.id) return this.action('open_target', s, candidate)
      return s.challenge && s.click_ready ? this.action('challenge', s, candidate) : wait('等待怪物挑战按钮')
    }
    if (s.targets.some(t => t.fighting && this.started.has(t.id))) return wait('等待本轮战斗结束')
    if (this.loops && ++this.wins >= this.loops) return done(`遍历完成，已确认发起 ${this.sent} 次挑战`)
    if (this.loops) this.visited.clear()
    this.ready = now + 10000
    return wait('本轮结束，等待刷新怪物')
  }
  resume() { if (this.pending) this.pending.at = this.now() }
}
